import React, { useState, useEffect, useMemo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import {
  ModalOverlay,
  StyledSearchModal,
  StyledInput,
  CalendarWrapper,
  ButtonWrapper,
  GuestSelectorWrapper,
  GuestSelectorButton,
  GuestCount,
} from "./styles";
import assets from "../../assets/index";
import { Button } from "../Button";
import { FlexDiv } from "../FlexDiv";
import { useProperties, useGetUnitAvailability, useTheme } from "../../hooks";
import { useBookingEngine } from "../../context/BookingEngineContext";
import {
  useSearchState,
  useSearchDispatch,
} from "../../context/SearchFilterContext";

import { Value as CalendarValue } from "react-calendar/dist/cjs/shared/types";
import { useNavigate } from "react-router-dom";
import { useAvailableCities } from "../../hooks/useAvailableCities";

interface SearchModalProps {
  onClose: () => void;
}

const SearchModal = ({ onClose }: SearchModalProps) => {
  const { themeMode } = useTheme();
  const navigate = useNavigate();
  const availableCities = useAvailableCities();
  const searchState = useSearchState();
  const dispatch = useSearchDispatch();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const toUTCDate = (date: Date): Date => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
  };

  const startOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  );

  const endOfMonth = new Date(startOfMonth);
  endOfMonth.setDate(startOfMonth.getDate() + 35);

  const formattedStartOfMonth = startOfMonth.toISOString().split("T")[0];
  const formattedEndOfMonth = endOfMonth.toISOString().split("T")[0];

  useEffect(() => {
    const defaultArrivingDate = new Date(
      Date.UTC(
        new Date().getUTCFullYear(),
        new Date().getUTCMonth(),
        new Date().getUTCDate()
      )
    );

    const defaultDepartureDate = new Date(
      Date.UTC(
        new Date().getUTCFullYear(),
        new Date().getUTCMonth(),
        new Date().getUTCDate() + 7
      )
    );

    dispatch({ type: "SET_ARRIVING_DATE", payload: defaultArrivingDate });
    dispatch({ type: "SET_DEPARTURE_DATE", payload: defaultDepartureDate });
  }, [dispatch]);

  const filters = useMemo(() => {
    return {
      cityName: searchState.cityName || "",
      availableFrom: searchState.arrivingDate
        ? toUTCDate(searchState.arrivingDate).toISOString().split("T")[0]
        : "",
      availableTo: searchState.departureDate
        ? toUTCDate(searchState.departureDate).toISOString().split("T")[0]
        : "",
      accommodates:
        searchState.numberOfAdults + searchState.numberOfChildren || 0,
    };
  }, [searchState]);

  // Use the useProperties hook to fetch filtered properties
  const {
    properties,
    loading: propertiesLoading,
    error: propertiesError,
  } = useProperties(filters);

  const [selectingRange] = useState(false);
  const { bookingEngineData } = useBookingEngine();

  const [tempStartDate] = useState<Date | null>(null);

  const [showGuestSelector, setShowGuestSelector] = useState(false);

  // Check if there is only one unit
  const isSingleUnit = bookingEngineData?.buildingsCount === 1;

  const { unitAvailability } = useGetUnitAvailability(
    properties[0]?.units[0]?.uid || "",
    formattedStartOfMonth,
    formattedEndOfMonth
  );

  const isDateAvailable = (date: Date): boolean => {
    const formattedDateStr = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0];

    if (unitAvailability) {
      return unitAvailability[formattedDateStr]?.availability > 0;
    }
    return true;
  };

  const getDatesBetween = (startDate: Date, endDate: Date): Date[] => {
    const dates: Date[] = [];
    let currentDate = new Date(
      Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      )
    );

    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    return dates;
  };

  const isRangeAvailable = (startDate: Date, endDate: Date): boolean => {
    const dates = getDatesBetween(startDate, endDate);
    return dates.every((date) => isDateAvailable(date));
  };

  const handleDateSelect = (value: CalendarValue) => {
    if (Array.isArray(value)) {
      const [startDate, endDate] = value;

      if (startDate && endDate && isRangeAvailable(startDate, endDate)) {
        dispatch({ type: "SET_ARRIVING_DATE", payload: toUTCDate(startDate) });
        dispatch({ type: "SET_DEPARTURE_DATE", payload: toUTCDate(endDate) });
      } else {
        console.warn("Selected range contains unavailable dates.");
      }
    } else if (value && isDateAvailable(value)) {
      dispatch({ type: "SET_ARRIVING_DATE", payload: toUTCDate(value) });
      dispatch({ type: "SET_DEPARTURE_DATE", payload: null });
    } else {
      console.warn("Selected date is unavailable.");
    }
  };

  const tileDisabled = ({ date }: { date: Date }): boolean => {
    const todayUTC = new Date(
      Date.UTC(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      )
    );

    if (isSingleUnit) {
      if (date < todayUTC) {
        return true;
      }

      const formattedDateStr = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      )
        .toISOString()
        .split("T")[0];

      return unitAvailability
        ? unitAvailability[formattedDateStr]?.availability === 0
        : false;
    }

    return date < todayUTC;
  };

  const tileClassName = ({
    date,
    view,
  }: {
    date: Date;
    view: string;
  }): string => {
    if (view !== "month") return "";

    if (
      searchState.arrivingDate &&
      searchState.departureDate &&
      date >= searchState.arrivingDate &&
      date <= searchState.departureDate
    ) {
      return "in-range";
    }

    if (selectingRange && tempStartDate) {
      if (date >= tempStartDate) {
        const isValidRange = isRangeAvailable(tempStartDate, date);
        return isValidRange ? "possible-range" : "invalid-range";
      }
    }

    return isDateAvailable(date) ? "available" : "unavailable";
  };

  const tileContent = ({ date }: { date: Date }) => {
    if (!isSingleUnit) return null;

    const formattedDateStr = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0];
    const availabilityInfo =
      unitAvailability && unitAvailability[formattedDateStr];

    if (availabilityInfo) {
      return (
        <div
          style={{
            textAlign: "center",
            position: "relative",
          }}
        >
          <span
            style={{
              fontSize: "9px",
              position: "absolute",
              display: "block",
              top: "-14px",
              right: "2px",
              left: "2px",
            }}
          >
            {availabilityInfo.price ? `$${availabilityInfo.price}` : "-"}
          </span>
        </div>
      );
    }
    return null;
  };

  const togglePets = () => {
    dispatch({ type: "TOGGLE_PETS" });
  };

  const handleGuestsPlaceholder = () => {
    const { numberOfAdults, numberOfChildren } = searchState;
    const totalGuests = numberOfAdults + numberOfChildren;

    if (totalGuests === 0) return "Select guests";
    return `${numberOfAdults} Adult${numberOfAdults > 1 ? "s" : ""}${
      numberOfChildren > 0
        ? `, ${numberOfChildren} Child${numberOfChildren > 1 ? "ren" : ""}`
        : ""
    }`;
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch({ type: "SET_CITY_NAME", payload: e.target.value });
  };

  const handleSearch = () => {
    // Convert all filter values to strings
    const stringifiedFilters = Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [key, String(value)])
    );

    // Create query parameters from the stringified filters
    const queryParams = new URLSearchParams(stringifiedFilters);
    navigate(`/results?${queryParams.toString()}`, {
      state: { properties, propertiesLoading, propertiesError },
    });
    onClose();
  };

  const handleMonthChange = ({
    action,
    activeStartDate,
    value,
    view,
  }: {
    action: string;
    activeStartDate: Date | null;
    value: CalendarValue;
    view: string;
  }) => {
    if (activeStartDate) {
      setCurrentMonth(activeStartDate);
    }
  };

  return (
    <>
      <ModalOverlay onClick={onClose}></ModalOverlay>
      <StyledSearchModal>
        <FlexDiv alignitems="center" justifycontent="space-between">
          <h2>Find my perfect stay</h2>
          <span
            onClick={onClose}
            style={{
              cursor: "pointer",
              filter: themeMode === "dark" ? "invert(1)" : "invert(0)",
            }}
          >
            <img alt="close-icon" src={assets.CloseIcon} height={20} />
          </span>
        </FlexDiv>

        <div>
          <StyledInput
            as="select"
            name="cityName"
            value={searchState.cityName}
            onChange={handleCityChange}
          >
            <option value="" disabled>
              I am flexible
            </option>
            {availableCities.map((city, index) => (
              <option key={index} value={city}>
                {city}
              </option>
            ))}
          </StyledInput>
        </div>

        <div style={{ position: "relative", width: "100%" }}>
          <StyledInput
            type="text"
            name="accommodates"
            placeholder="Who is coming?"
            value={handleGuestsPlaceholder()}
            onClick={() => setShowGuestSelector((prev) => !prev)}
            readOnly
          />
          {showGuestSelector && (
            <span
              onClick={() => setShowGuestSelector(false)}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
              }}
            >
              <img src={assets.CloseIcon} height={14} alt="close icon" />
            </span>
          )}
        </div>

        {showGuestSelector && (
          <GuestSelectorWrapper>
            <h4>Who's coming?</h4>
            <FlexDiv justifycontent="space-between" alignitems="center">
              <span style={{ flex: 1 }}>Adults</span>
              <GuestSelectorButton
                onClick={() =>
                  dispatch({
                    type: "SET_NUMBER_OF_ADULTS",
                    payload: Math.max(0, searchState.numberOfAdults - 1),
                  })
                }
              >
                -
              </GuestSelectorButton>
              <GuestCount>{searchState.numberOfAdults}</GuestCount>
              <GuestSelectorButton
                onClick={() =>
                  dispatch({
                    type: "SET_NUMBER_OF_ADULTS",
                    payload: searchState.numberOfAdults + 1,
                  })
                }
              >
                +
              </GuestSelectorButton>
            </FlexDiv>
            <div style={{ height: 20 }}></div>
            <FlexDiv justifycontent="space-between" alignitems="center">
              <span style={{ flex: 1 }}>Children</span>
              <GuestSelectorButton
                onClick={() =>
                  dispatch({
                    type: "SET_NUMBER_OF_CHILDREN",
                    payload: Math.max(0, searchState.numberOfChildren - 1),
                  })
                }
              >
                -
              </GuestSelectorButton>
              <GuestCount>{searchState.numberOfChildren}</GuestCount>
              <GuestSelectorButton
                onClick={() =>
                  dispatch({
                    type: "SET_NUMBER_OF_CHILDREN",
                    payload: searchState.numberOfChildren + 1,
                  })
                }
              >
                +
              </GuestSelectorButton>
            </FlexDiv>
            <div style={{ height: 20 }}></div>
            <FlexDiv justifycontent="space-between">
              <span style={{ flex: 1 }}>Pets</span>
              <label style={{ cursor: "pointer" }}>
                <input
                  style={{ cursor: "pointer" }}
                  type="checkbox"
                  checked={searchState.withPets}
                  onChange={togglePets}
                />
                Yes
              </label>
            </FlexDiv>
          </GuestSelectorWrapper>
        )}

        {!showGuestSelector && (
          <CalendarWrapper>
            <Calendar
              onChange={handleDateSelect}
              value={
                searchState.arrivingDate && searchState.departureDate
                  ? [searchState.arrivingDate, searchState.departureDate]
                  : searchState.arrivingDate
              }
              selectRange
              minDate={new Date()}
              tileDisabled={tileDisabled}
              tileContent={tileContent}
              tileClassName={tileClassName}
              onActiveStartDateChange={handleMonthChange}
            />
          </CalendarWrapper>
        )}

        <ButtonWrapper>
          <Button onClick={handleSearch} style={{ height: 50 }}>
            Explore <img src={assets.ArrowRightIcon} alt="arrow right " />
          </Button>
        </ButtonWrapper>
      </StyledSearchModal>
    </>
  );
};

export default SearchModal;
